import { MotionConfig } from "motion/react";
import { type CSSProperties, PropsWithChildren, useCallback, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "src/authentication/hooks/useAuth";
import { FloatingNavBar } from "./frame/FloatingNavBar";
import { SPATIAL_EASE } from "./frame/frameMotion";
import { MoreSheet } from "./frame/MoreSheet";
import { NavigationRail, RAIL_WIDTH } from "./frame/NavigationRail";

const RAIL_OPEN_KEY = "lbc-hub-rail-open";

const readRailOpen = () => {
    try {
        return localStorage.getItem(RAIL_OPEN_KEY) === "true";
    } catch {
        return false;
    }
};

interface Props {}

export const NavBarLayout = (props: PropsWithChildren<Props>) => {
    const [moreOpen, setMoreOpen] = useState(false);
    const [railOpen, setRailOpen] = useState(readRailOpen);

    const { person } = useAuth();
    const { pathname } = useLocation();

    // `from` is the `from` query parameter
    const from = new URLSearchParams(window.location.search).get("from");

    useEffect(() => {
        setMoreOpen(false);
    }, [pathname]);

    const toggleRail = useCallback(() => {
        setRailOpen((open) => {
            try {
                localStorage.setItem(RAIL_OPEN_KEY, String(!open));
            } catch {
                // an unsaved choice only means the rail starts collapsed next time
            }

            return !open;
        });
    }, []);

    const closeMore = useCallback(() => setMoreOpen(false), []);

    if (person === null) {
        return <Navigate to={`/set-up-your-profile${from ? `?from=${encodeURIComponent(from)}` : ""}`} replace />;
    }

    return (
        <MotionConfig reducedMotion="user">
            <div
                data-app-frame
                className="min-h-screen lg:bg-frame"
                style={{ "--rail-width": railOpen ? RAIL_WIDTH.expanded : RAIL_WIDTH.collapsed } as CSSProperties}
            >
                <NavigationRail open={railOpen} onToggle={toggleRail} />

                {/* Covers the gap above the page's pane, so nothing scrolls into view behind the app bar */}
                <div className="hidden lg:block fixed top-0 inset-x-0 h-2 bg-frame z-[46]" aria-hidden="true" />

                <div
                    className="lg:ml-[var(--rail-width)] lg:p-2 lg:pl-0 pb-[var(--frame-bottom)] transition-[margin] duration-500 motion-reduce:transition-none"
                    style={{ transitionTimingFunction: SPATIAL_EASE }}
                >
                    {/* On a laptop the page is a rounded pane on the frame's shade */}
                    <div className="min-h-screen lg:min-h-[calc(100vh-1rem)] bg-base-200 lg:rounded-3xl">
                        {props.children ?? <Outlet />}
                    </div>
                </div>

                <FloatingNavBar moreOpen={moreOpen} onMore={() => setMoreOpen((open) => !open)} />

                <MoreSheet open={moreOpen} onClose={closeMore} />
            </div>
        </MotionConfig>
    );
};
